'use client'
import { Button } from '@/components/ui/button'
import { HeroWrapper } from '@/components/ui/hero-wrapper'
import { useRouter } from '@/i18n/navigation'
import { ICarouselProps } from '@/lib/types/hero-slider.types'
import { cn } from '@/lib/utils'
import Autoplay from 'embla-carousel-autoplay'
import useEmblaCarousel from 'embla-carousel-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useCallback, useSyncExternalStore } from 'react'

export function HeroSlider({ slides }: ICarouselProps) {
  const t = useTranslations('Home')
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 50 }, [
    Autoplay({ playOnInit: true, delay: 3500 })
  ])

  const router = useRouter()

  const subscribe = useCallback(
    (callback: () => void) => {
      if (!emblaApi) return () => {}
      emblaApi.on('select', callback)
      return () => emblaApi.off('select', callback)
    },
    [emblaApi]
  )
  const selectedIndex = useSyncExternalStore(
    subscribe,
    () => emblaApi?.selectedScrollSnap() ?? 0,
    () => 0
  )

  const scrollTo = useCallback(
    (index: number) => {
      if (!emblaApi) return
      emblaApi.scrollTo(index)
    },
    [emblaApi]
  )

  const filteredSlides = slides
    .filter((slide) => slide.visible)
    .sort((a, b) => a.order - b.order)

  return (
    <div className="relative w-full">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex lg:h-175">
          {filteredSlides.map((slide, index) => (
            <HeroWrapper
              onClick={() => {
                if (slide.href) {
                  router.push(slide.href)
                }
              }}
              key={index}
              className={cn('cursor-pointer', slide.bgColor)}
            >
              <div className="mt-10 mb-11 flex-1 px-4 text-center md:max-w-180 lg:text-left">
                <p
                  className={cn(
                    'md:text-heading-2 mb-4 text-[26px] leading-8 font-black md:leading-16',
                    slide.titleColor
                  )}
                >
                  {t(slide.title)}
                </p>

                {slide.content && (
                  <p
                    className={cn(
                      'text-body-3 md:text-body-2 leading-4.75 md:leading-6',
                      slide.contentColor
                    )}
                  >
                    {t(slide.content)}
                  </p>
                )}
                {slide.href && slide.buttonText && (
                  <Button
                    href={slide.href}
                    variant={slide.buttonVariant}
                    label={t(slide.buttonText)}
                    className={cn(
                      'text-body-3 mx-auto mt-8 w-fit md:flex lg:mx-0',
                      slide.buttonTextColor
                    )}
                  />
                )}
              </div>

              <Image
                src={slide.image}
                alt={t(slide.title)}
                width={640}
                height={400}
                fetchPriority={index === 0 ? 'high' : 'low'}
                priority={index === 0}
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 544px"
                className={cn(
                  'mx-auto mt-auto px-4 md:max-w-90 lg:ml-auto lg:max-w-160',
                  slide.imageMarginBottom && `mb-${slide.imageMarginBottom}`
                )}
              />
            </HeroWrapper>
          ))}
        </div>
      </div>

      <div className="mt-4 flex justify-center gap-2.5">
        {filteredSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollTo(index)}
            className={cn(
              'h-2 cursor-pointer rounded-full transition-all duration-200',
              index === selectedIndex
                ? 'bg-secondary-500 w-7'
                : 'hover:bg-tertiary-700-400 w-3.5 bg-gray-300'
            )}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  )
}
